import { useCallback } from 'react';

import type { ExperimentConfig, ResolvedExperimentData } from '@change-corgi/experiments/configs';

import { useTrackOnce } from './useTrackOnce';

type Options = Readonly<{
	/**
	 * Reset keys that re-fire the event when changed. Required when the same component instance
	 * is reused across logical contexts (e.g. SPA petition change with no remount), since
	 * variations are deterministic per user and would otherwise dedup the legitimate fire.
	 */
	resetKeys?: readonly unknown[];
}>;

/**
 * Returns a function that fires `experiment_started` with `experiment_name` and `variant_name`
 * for the given experiment. No-ops when `experiment.treated === false`. Deduped per mount via
 * `useTrackOnce`. Treated control users fire too (control is a treatment arm for lift analysis).
 *
 * @example
 * const trackExperimentStarted = useTrackExperimentStarted(experiment);
 * useEffect(() => { void trackExperimentStarted(); }, [trackExperimentStarted]);
 */
export function useTrackExperimentStarted<E extends ExperimentConfig>(
	experiment: ResolvedExperimentData<E>,
	{ resetKeys = [] }: Options = {},
): () => Promise<boolean> {
	const { experimentName, variation, treated } = experiment;

	const trackOnce = useTrackOnce([experimentName, variation, ...resetKeys]);

	return useCallback(async (): Promise<boolean> => {
		if (!treated) return false;
		return trackOnce('experiment_started', {
			experiment_name: experimentName,
			variant_name: variation,
		});
	}, [trackOnce, treated, experimentName, variation]);
}
